import { faqsPl } from "@data/audyt-embedded-landing/faq";
import {
  aboutUsCopyPl,
  contactFormPl,
  faqStructuredData,
  iotClusterLinks,
  iotKnowledgeBaseLinks,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/iot-landing/shared";
import {
  Battery,
  Calendar,
  CheckCircle,
  CircuitBoard,
  Code,
  FileSearch,
  FileText,
  Layers,
  LifeBuoy,
  PenTool,
  RefreshCw,
  Rocket,
  Search,
  ShieldCheck,
  Wrench,
} from "lucide-react";

import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

export const title =
  "Audyt elektroniki i firmware - konsultacje dla produktów embedded";

export const description =
  "Audyt firmware i audyt elektroniki przed skalowaniem produkcji — konsultacje embedded z raportem ryzyk, planem napraw i opcją przejęcia rozwoju. Code review po NDA, analiza stabilności, baterii i architektury IoT.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "Audyt elektroniki i firmware",
    description,
    "Embedded electronics and firmware audit",
  ),
  faqStructuredData(faqsPl),
];

export const heroCopy: HeroCopy = {
  header: "Audyt elektroniki",
  headerTail: "i firmware",
  description:
    "Niezależny audyt elektroniki, firmware i architektury produktów embedded — zanim zainwestujesz w produkcję seryjną, zmienisz dostawcę albo wejdziesz w rundę inwestycyjną. Raport ryzyk, plan napraw i opcja przejęcia rozwoju po NDA.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Poznaj nasz proces",
    href: "#proces",
  },
  benefits: [
    { text: "Raport ryzyk i rekomendacje", Icon: CheckCircle },
    { text: "Audyt po NDA z dostępem do kodu", Icon: CheckCircle },
    { text: "Opcja przejęcia rozwoju", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Kiedy warto zamówić audyt elektroniki i firmware?",
    p: "Audyt embedded to nie luksus — to ubezpieczenie przed kosztownymi błędami w produkcji, na styku urządzenia z aplikacją albo po przejęciu kodu od poprzedniego dostawcy. Czy któryś z tych scenariuszy brzmi znajomo?",
  },
  points: [
    {
      Icon: Rocket,
      title: "Planujesz skalowanie produkcji i chcesz wiedzieć, czy hardware jest gotowy",
      description:
        "Prototyp działa na biurku, ale nie wiesz, czy PCB, BOM i firmware wytrzymają produkcję seryjną — audyt elektroniki i firmware przed zamówieniem pierwszej partii.",
    },
    {
      Icon: RefreshCw,
      title: "Zmieniasz dostawcę i przejmujesz kod w spadku",
      description:
        "Poprzedni vendor zostawił repozytorium, schemat i dokumentację — potrzebujesz niezależnej oceny jakości kodu, architektury i ryzyk technicznych.",
    },
    {
      Icon: Battery,
      title: "Masz problemy ze stabilnością lub zużyciem baterii",
      description:
        "Urządzenie resetuje się, traci połączenie albo rozładowuje baterię za szybko — audyt firmware pod kątem poboru prądu, race conditions i zarządzania energią.",
    },
    {
      Icon: ShieldCheck,
      title: "Przed rundą inwestycyjną lub due diligence technicznym",
      description:
        "Inwestorzy pytają o stan techniczny produktu — potrzebujesz ustrukturyzowanego raportu z oceną ryzyk, rekomendacjami i szacunkiem prac naprawczych.",
    },
    {
      Icon: Layers,
      title: "Problemy na styku urządzenia, aplikacji i chmury",
      description:
        "Firmware działa, aplikacja też — ale integracja szwankuje. Rozszerzamy audyt o warstwę app/backend, bo w produktach IoT problem często leży między warstwami.",
    },
  ],
  cta: {
    h4: "Brzmi znajomo?",
    text: "Opowiedz nam o produkcie i problemie — nawet jeśli nie masz jeszcze dostępu do kodu. Podpowiemy, jaki zakres audytu ma sens.",
    href: "#kontakt",
    buttonText: "Umów darmową konsultację",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Nasza specjalizacja",
    h3: "Dlaczego warto zamówić audyt embedded u nas?",
    p: "Konsultacje embedded to coś więcej niż code review — dajemy ustrukturyzowany raport z ryzykami, dowodami, priorytetami i planem napraw. A po audycie możemy przejąć rozwój, jeśli tego potrzebujesz.",
    imageAlt: "Audyt elektroniki i firmware — konsultacje embedded",
  },
  values: [
    {
      Icon: FileSearch,
      title: "Ustrukturyzowany raport, nie luźne uwagi",
      description:
        "Audyt kończy się raportem z listą ryzyk, priorytetami (krytyczne / ważne / kosmetyczne), dowodami w kodzie i schemacie oraz szacunkiem prac naprawczych.",
    },
    {
      Icon: CircuitBoard,
      title: "Audyt elektroniki i firmware razem",
      description:
        "Przeglądamy schemat, PCB, BOM i kod firmware — bo problemy z baterią czy stabilnością często wynikają ze współdziałania hardware i software.",
    },
    {
      Icon: Code,
      title: "Code review po NDA z dostępem do repozytorium",
      description:
        "Podpisujemy NDA, analizujemy kod źródłowy, historię commitów, testy i pipeline CI/CD — nie audytujemy z opisu bez dostępu do materiałów.",
    },
    {
      Icon: Battery,
      title: "Diagnostyka problemów z energią i stabilnością",
      description:
        "Typowe bolączki produktów IoT — reset, utrata połączenia, szybkie rozładowanie — diagnozujemy na poziomie firmware, hardware i architektury komunikacji.",
    },
    {
      Icon: Wrench,
      title: "Od audytu do naprawy — jeden zespół",
      description:
        "Po audycie możemy przejąć rozwój — refaktoryzacja, OTA, dokończenie funkcji albo pełne domknięcie produktu. Nie zostawiamy Cię z samym raportem.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "Audyt elektroniki, firmware i konsultacje embedded",
    p: "Niezależna ocena produktu embedded przed produkcją, zmianą dostawcy lub inwestycją. Zakres dopasowujemy do sytuacji — od wąskiego code review po pełny audyt systemu IoT.",
  },
  services: [
    {
      Icon: Search,
      title: "Wstępna konsultacja i określenie zakresu",
      description:
        "Omawiamy produkt, problem i dostępne materiały — definiujemy, czy potrzebujesz audytu firmware, elektroniki, OTA czy pełnej architektury IoT.",
    },
    {
      Icon: Code,
      title: "Audyt firmware i code review",
      description:
        "Przegląd kodu źródłowego, architektury, zarządzania pamięcią, obsługi błędów, testów i pipeline'u aktualizacji OTA/DFU.",
    },
    {
      Icon: CircuitBoard,
      title: "Audyt elektroniki i PCB",
      description:
        "Przegląd schematu, layoutu PCB, BOM, zasilania, EMC i przygotowania pod produkcję — tam, gdzie mamy dostęp do plików projektowych.",
    },
    {
      Icon: Layers,
      title: "Audyt architektury produktu IoT",
      description:
        "Ocena komunikacji urządzenie–aplikacja–backend, bezpieczeństwa, skalowalności floty i zgodności z RODO w produktach connected.",
    },
    {
      Icon: Battery,
      title: "Diagnostyka problemów z baterią i stabilnością",
      description:
        "Analiza poboru prądu, trybów uśpienia, reconnectu łączności i race conditions — z rekomendacjami naprawczymi i priorytetami.",
    },
    {
      Icon: ShieldCheck,
      title: "Audyt bezpieczeństwa OTA i komunikacji",
      description:
        "Weryfikacja podpisu firmware, szyfrowania, zarządzania certyfikatami i mechanizmów rollback w aktualizacjach OTA.",
    },
    {
      Icon: FileText,
      title: "Raport z ryzykami i planem napraw",
      description:
        "Ustrukturyzowany dokument z listą ryzyk, priorytetami, szacunkiem prac naprawczych i rekomendacją kolejnych kroków.",
    },
    {
      Icon: LifeBuoy,
      title: "Przejęcie rozwoju po audycie",
      description:
        "Realizujemy plan napraw — refaktoryzacja firmware, poprawki PCB, wdrożenie OTA albo dokończenie produktu embedded od miejsca, w którym audyt go zostawił.",
    },
  ],
  cta: {
    h4: "Nie wiesz, jaki zakres audytu wybrać?",
    p: "Opisz nam produkt i problem — nawet bez dostępu do kodu. Podpowiemy, czy wystarczy wąski przegląd, czy potrzebujesz pełnego audytu embedded.",
    button: {
      text: "Umów darmową konsultację",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Jak wygląda audyt elektroniki i firmware",
    p: "Od NDA i dostępu do materiałów po raport z ryzykami i plan napraw. Przejrzyste etapy i stała wycena zakresu.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Darmowe spotkanie wstępne",
      duration: "w kilka godzin",
      description:
        "30 minut rozmowy — opisujesz produkt, problem i dostępne materiały. Ustalamy, czy audyt ma sens i jaki zakres będzie optymalny.",
    },
    {
      Icon: PenTool,
      title: "NDA i dostęp do materiałów",
      duration: "1–3 dni",
      description:
        "Podpisujemy NDA, otrzymujemy repozytorium, schemat, dokumentację i ewentualnie egzemplarz urządzenia do testów.",
    },
    {
      Icon: FileText,
      title: "Definicja zakresu i wycena",
      duration: "w kilka dni",
      description:
        "Precyzujemy, co audytujemy — firmware, PCB, OTA, architektura IoT — i podajemy stałą wycenę zakresu.",
    },
    {
      Icon: FileSearch,
      title: "Przegląd kodu i dokumentacji",
      duration: "1–2 tygodnie",
      description:
        "Analizujemy kod firmware, schemat, PCB, testy i pipeline CI/CD. Identyfikujemy ryzyka i zbieramy dowody.",
    },
    {
      Icon: Search,
      title: "Testy urządzenia (jeśli dostępne)",
      duration: "kilka dni",
      description:
        "Testujemy egzemplarz urządzenia pod kątem stabilności, poboru prądu, łączności i zachowania w terenie.",
    },
    {
      Icon: ShieldCheck,
      title: "Ocena bezpieczeństwa i zgodności",
      duration: "równolegle z przeglądem",
      description:
        "Weryfikujemy OTA, szyfrowanie, certyfikaty i przygotowanie pod CE — tam, gdzie wchodzi to w zakres audytu.",
    },
    {
      Icon: FileText,
      title: "Raport i prezentacja wyników",
      duration: "2–3 dni",
      description:
        "Dostarczamy raport z ryzykami, priorytetami, rekomendacjami i szacunkiem prac naprawczych. Omawiamy wyniki na spotkaniu.",
    },
    {
      Icon: CheckCircle,
      title: "Plan naprawy i opcjonalne przejęcie rozwoju",
      duration: "na stałe",
      description:
        "Decydujesz, czy realizujesz rekomendacje wewnętrznie, czy przekazujesz nam plan naprawy do wdrożenia.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Zamów audyt embedded",
  },
};

export const aboutUsCopy = aboutUsCopyPl({
  imageAlt: "Zespół Blues Brackets — audyt elektroniki i firmware",
});

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania o audyt elektroniki i firmware",
  },
  faqs: faqsPl,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Potrzebujesz audytu elektroniki lub firmware?",
    p: "Umów się na darmową konsultację — opowiedz o produkcie i problemie, a zaproponujemy zakres audytu embedded.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "Audyt firmware i audyt elektroniki — konsultacje embedded z raportem ryzyk, planem napraw i opcją przejęcia rozwoju.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Nasze usługi" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "#faq", text: "FAQ" },
      { href: "/iot", text: "Systemy IoT" },
      ...iotClusterLinks.filter(
        l => l.href !== "/iot" && l.href !== "/audyt-elektroniki-i-firmware",
      ),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [...iotKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: contactFormPl,
};
